import {
  CreateCustomContentRequest,
  UpdateCustomContentRequest,
  AssignCustomContentRequest,
  CustomContentDTO,
  CustomContentAssignmentDTO,
} from '@hmedia/legenda-ds-api-client'
import { useMutation, useQuery } from 'react-query'
import API from '../../services/DSApiService'
import type { CustomContentAdminDTO, CustomContentAssignmentAdminDTO } from './models'

const getAllCustomContents = async (): Promise<CustomContentAdminDTO[]> =>
  (await API.getAllCustomContents()).data.map((model) => convertCustomContentModelToDTO(model))

const getCustomContent = async (id: string): Promise<CustomContentAdminDTO> =>
  convertCustomContentModelToDTO((await API.getCustomContent(id)).data)

const createCustomContent = async (request: CreateCustomContentRequest): Promise<CustomContentAdminDTO> =>
  convertCustomContentModelToDTO((await API.createCustomContent(request)).data)

const updateCustomContent = async (id: string, request: UpdateCustomContentRequest): Promise<CustomContentAdminDTO> =>
  convertCustomContentModelToDTO((await API.updateCustomContent(id, request)).data)

const deleteCustomContent = async (id: string): Promise<void> => {
  await API.deleteCustomContent(id)
}

const getCustomContentsForScreen = async (screenId: string): Promise<CustomContentAssignmentAdminDTO[]> =>
  (await API.getCustomContentsForScreen(screenId)).data.map((model) => convertCustomContentAssignmentModelToDTO(model))

const assignCustomContentToScreen = async (screenId: string, request: AssignCustomContentRequest) =>
  await API.assignCustomContentToScreen(screenId, request)

const removeCustomContentFromScreen = async (screenId: string, request: AssignCustomContentRequest) =>
  await API.removeCustomContentFromScreen(screenId, request)


export const useGetAllCustomContentsQuery = () =>
  useQuery(['customContents'], () => getAllCustomContents())

export const useGetCustomContentQuery = (id: string) =>
  useQuery(['customContent', id], () => getCustomContent(id), { enabled: !!id })

export const useCreateCustomContentMutation = (
  onSuccess?: (data: CustomContentAdminDTO) => void,
  onError?: (error: any) => void,
) =>
  useMutation((request: CreateCustomContentRequest) => createCustomContent(request), {
    onSuccess,
    onError,
  })

export const useUpdateCustomContentMutation = (
  onSuccess?: (data: CustomContentAdminDTO) => void,
  onError?: (error: any) => void,
) =>
  useMutation(
    ({ id, request }: { id: string; request: UpdateCustomContentRequest }) =>
      updateCustomContent(id, request),
    {
      onSuccess,
      onError,
    }
  )

export const useDeleteCustomContentMutation = (
  onSuccess?: () => void,
  onError?: (error: any) => void,
) =>
  useMutation((id: string) => deleteCustomContent(id), {
    onSuccess,
    onError,
  })

export const useGetCustomContentsForScreenQuery = (screenId: string) =>
  useQuery(['customContentsForScreen', screenId], () => getCustomContentsForScreen(screenId), {
    enabled: !!screenId,
  })

export const useAssignCustomContentToScreenMutation = (
  onSuccess?: () => void,
  onError?: (error: any) => void,
) =>
  useMutation(
    ({ screenId, request }: { screenId: string; request: AssignCustomContentRequest }) =>
      assignCustomContentToScreen(screenId, request),
    {
      onSuccess,
      onError,
    }
  )

export const useRemoveCustomContentFromScreenMutation = (
  onSuccess?: () => void,
  onError?: (error: any) => void,
) =>
  useMutation(
    ({ screenId, request }: { screenId: string; request: AssignCustomContentRequest }) =>
      removeCustomContentFromScreen(screenId, request),
    {
      onSuccess,
      onError,
    }
  )


const convertCustomContentModelToDTO = (model: CustomContentDTO): CustomContentAdminDTO => ({
  ...model,
  key: model.id,
})

const convertCustomContentAssignmentModelToDTO = (
  model: CustomContentAssignmentDTO
): CustomContentAssignmentAdminDTO => ({
  ...model,
  key: model.id,
})
